import { useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { emptyFilterCriteria, type FilterState } from '@/features/filterSlice';
import { isFilterEmpty, pickFilterCriteria } from '@/features/filterHelpers';
import { useFetchNetworkGroupsQuery } from '@/features/networkGroupSlice';
import { getGroupNameFromId } from '@/helpers/networkGroups';
import { useAnalytics } from '@/shell/Analytics';

type FilterSource =
  | 'filter_form'
  | 'group_buttons'
  | 'network_buttons'
  | 'dashboard'
  | 'red_flag_card'
  | 'no_results';

const emptyValues = new Map(Object.entries(emptyFilterCriteria));

export const useTrackFilterChange = () => {
  const { track } = useAnalytics();
  const { data: networkGroups } = useFetchNetworkGroupsQuery();

  return useCallback(
    (filter: FilterState, source: FilterSource) => {
      if (isFilterEmpty(filter)) {
        track('filter_cleared', { source });
        return;
      }

      const criteria = pickFilterCriteria(filter);
      const applied = Object.entries(criteria).filter(
        ([key, value]) => !isEqual(value, emptyValues.get(key))
      );
      const groups = Object.keys(criteria.groups).flatMap(
        (id) => getGroupNameFromId(id, networkGroups) ?? []
      );

      track('filter_applied', {
        source,
        criteria: applied.map(([key]) => key),
        criteria_count: applied.length,
        ...Object.fromEntries(
          applied.filter(([key]) => key !== 'groups' && key !== 'tags')
        ),
        ...(groups.length > 0 && { groups }),
      });
    },
    [track, networkGroups]
  );
};
